import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';

import {ColumnGroup} from 'common/components/data-table/types';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {FunnelBookmark} from 'common/report/funnels/models/types';
import {getBookmarkWithAppliedDefaults} from 'common/report/funnels/util/bookmark-overrides';
import {
  isCohortSegment,
  isDateSegment,
  isNumericSegment,
  isOtherSegment,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import {isGroupedEntry} from 'common/query-builder/query-entry-section/types';
import {mapBookmarkStepsToEntries} from 'common/report/funnels/models/format';
import {getDataGroupName} from 'common/report/funnels/util';

import {StatSigClickHandler} from '../cell-renderers';
import {FunnelStepBookmarkType, FunnelTableRow} from '../types';
import {FunnelsStepProjectDetails, getDataTableInfo, getStepChartData, StepChartData} from '../util';
import {StepChartBookmarkController} from './index';
import {
  COHORT_BREAKDOWN_REASON,
  DATE_PROPERTY_BREAKDOWN_REASON,
  MESSAGE_PREFIX_VIEW_FLOWS,
  NUMERIC_PROPERTY_BREAKDOWN_REASON,
  TOP_PATHS_BREAKDOWN_REASON,
} from './constants';

// TODO once the behavior and metrics project is over we will remove this
export class FunnelsStepChartBookmarkController implements StepChartBookmarkController {
  type = FunnelStepBookmarkType.FUNNELS;
  bookmark: FunnelBookmark;
  queryResults: FunnelQueryResponse;

  constructor(bookmark: FunnelBookmark, results: FunnelQueryResponse) {
    this.bookmark = bookmark;
    this.queryResults = results;
  }

  public getDisableViewInFlowsButtonMessage(): string {
    let reason: string = null;

    const isTopPaths = (segment: Segment): boolean => {
      if (!isOtherSegment(segment)) {
        return false;
      }
      const isTopPathsChart = segment.other.value === `$paths`;
      return isTopPathsChart;
    };

    const groupBy = this.bookmark.group_by;
    if (Array.isArray(groupBy)) {
      if (groupBy.some(isTopPaths)) {
        reason = TOP_PATHS_BREAKDOWN_REASON;
      } else if (groupBy.some(isNumericSegment)) {
        reason = NUMERIC_PROPERTY_BREAKDOWN_REASON;
      } else if (groupBy.some(isDateSegment)) {
        reason = DATE_PROPERTY_BREAKDOWN_REASON;
      } else if (groupBy.some(isCohortSegment)) {
        reason = COHORT_BREAKDOWN_REASON;
      }
    }

    const events = mapBookmarkStepsToEntries(this.bookmark.steps);
    if (Array.isArray(events) && events.some(isGroupedEntry)) {
      reason = `viewing event comparisons`;
    }

    return reason ? `${MESSAGE_PREFIX_VIEW_FLOWS} ${reason}` : ``;
  }

  getDisableViewUsersButtonMessage(): string {
    const messagePrefix = `View ${getDataGroupName()} is disabled when`;
    let reason: string = null;

    const isTopPaths = (segment: Segment): boolean => {
      if (!isOtherSegment(segment)) {
        return false;
      }
      const isTopPathsChart = segment.other.value === `$paths`;
      const isTopPathsBreakdown = segment.other.propertyName === `$paths`;
      return isTopPathsChart || isTopPathsBreakdown;
    };

    const groupBy = this.bookmark.group_by;
    if (Array.isArray(groupBy)) {
      if (groupBy.some(isTopPaths)) {
        reason = `breaking down by top path`;
      } else if (groupBy.some(isCohortSegment)) {
        reason = COHORT_BREAKDOWN_REASON;
      }
    }

    return reason ? `${messagePrefix} ${reason}` : ``;
  }

  public getBookmarkWithAppliedDefaults(bookmark: FunnelBookmark): FunnelBookmark {
    return getBookmarkWithAppliedDefaults(bookmark);
  }
  public getStepChartData({projectDetails}: {projectDetails: FunnelsStepProjectDetails}) {
    return getStepChartData(this.bookmark, this.queryResults, projectDetails);
  }

  public getSorting() {
    return this.bookmark.sorting;
  }

  public getDataTableInfo({
    stepChartData,
    projectDetails,
    tableWidth,
    onStatSigClick,
  }: {
    stepChartData: StepChartData;
    projectDetails: FunnelsStepProjectDetails;
    tableWidth: number;
    onStatSigClick: StatSigClickHandler;
  }): {rows: FunnelTableRow[]; columnGroups: ColumnGroup[]; comparisonRows?: FunnelTableRow[]} {
    return getDataTableInfo({
      bookmark: this.bookmark,
      projectDetails,
      onStatSigClick,
      formattedData: stepChartData.funnelSegments,
      formattedCompareData: stepChartData.funnelCompareSegments,
      formattedLiftData: stepChartData.funnelLiftSegments,
      isDownloadMode: false,
      savedLegend: stepChartData.savedLegend,
      tableWidth,
      showStepNamesInGroupHeaders: true,
    });
  }

  public isEmptyReport(): boolean {
    return isEmpty(this.queryResults?.data);
  }

  public isEquivalent(bookmark: any, results: any) {
    return isEqual(this.bookmark, bookmark) && isEqual(this.queryResults, results);
  }

  public getDataGroupId(): string {
    return this.bookmark.data_group_id;
  }

  public getPropSumLabel(): string {
    return ``; // only possible in multi-metric
  }
}
